<template>
  <div class="trip-log-visits-grid">
    <Grid
      class="trip-log-visits-grid__grid"
      :data-items="tripLogStore.tripVisits ?? []"
      :loader="loading"
      :columns="columns"
      ref="gridRef"
    >
      <template v-slot:TimeGettingToPlantCell="{ props: { dataItem } }">
        <td class="k-table-td">
          <div class="trip-log-visits-grid__input-wrapper">
            <NumericTextBox
              class="trip-log-visits-grid__input"
              :min="0"
              :max="1000"
              format="n0"
              v-model="dataItem.TimeToPlantHours"
              :spinners="false"
              :step="1"
              :inputSuffix="'suffix'"
              inputType="number"
              :disabled="!newTrip"
            >
              <template v-slot:suffix>
                <span>HH</span>
              </template>
            </NumericTextBox>
            :
            <NumericTextBox
              class="trip-log-visits-grid__input"
              :min="0"
              :max="59"
              format="n0"
              v-model="dataItem.TimeToPlantMinutes"
              :spinners="false"
              :step="1"
              :inputSuffix="'suffix'"
              inputType="number"
              :disabled="!newTrip"
            >
              <template v-slot:suffix>
                <span>MM</span>
              </template>
            </NumericTextBox>
          </div>
        </td>
      </template>
      <template v-slot:TimeInPlantCell="{ props: { dataItem } }">
        <td class="k-table-td">
          <div class="trip-log-visits-grid__input-wrapper">
            <NumericTextBox
              class="trip-log-visits-grid__input"
              :min="0"
              :max="1000"
              format="n0"
              v-model="dataItem.TimeInPlantHours"
              :spinners="false"
              :step="1"
              :inputSuffix="'suffix'"
              inputType="number"
              :disabled="!newTrip"
            >
              <template v-slot:suffix>
                <span>HH</span>
              </template>
            </NumericTextBox>
            :
            <NumericTextBox
              class="trip-log-visits-grid__input"
              :min="0"
              :max="59"
              format="n0"
              v-model="dataItem.TimeInPlantMinutes"
              :spinners="false"
              :step="1"
              :inputSuffix="'suffix'"
              inputType="number"
              :disabled="!newTrip"
            >
              <template v-slot:suffix>
                <span>MM</span>
              </template>
            </NumericTextBox>
          </div>
        </td>
      </template>
      <template v-slot:TimeGettingHomeCell="{ props: { dataItem } }">
        <td class="k-table-td">
          <div class="trip-log-visits-grid__input-wrapper">
            <NumericTextBox
              class="trip-log-visits-grid__input"
              :min="0"
              :max="1000"
              format="n0"
              v-model="dataItem.TimeToHomeHours"
              :spinners="false"
              :step="1"
              :inputSuffix="'suffix'"
              inputType="number"
              :disabled="!newTrip"
            >
              <template v-slot:suffix>
                <span>HH</span>
              </template>
            </NumericTextBox>
            :
            <NumericTextBox
              class="trip-log-visits-grid__input"
              :min="0"
              :max="59"
              format="n0"
              v-model="dataItem.TimeToHomeMinutes"
              :spinners="false"
              :step="1"
              :inputSuffix="'suffix'"
              inputType="number"
              :disabled="!newTrip"
            >
              <template v-slot:suffix>
                <span>MM</span>
              </template>
            </NumericTextBox>
          </div>
        </td>
      </template>
      <template v-slot:AllocationOfTotalExpensesCell="{ props: { dataItem } }">
        <td class="k-table-td">
          <input
            class="trip-log-visits-grid__allocation-input"
            v-model="dataItem.AllocationOfTotalExpenses"
            type="number"
            placeholder="0"
            min="0"
            step="0.01"
            :disabled="!newTrip"
          />
        </td>
      </template>
      <template v-slot:CommentCell="{ props: { dataItem } }">
        <td class="k-table-td">
          <textarea
            class="trip-log-visits-grid__comment"
            v-model="dataItem.Comment"
            rows="5"
            maxlength="500"
            data-cy="comment-input"
            :disabled="!newTrip"
          ></textarea>
        </td>
      </template>
    </Grid>
    <div class="trip-log-visits-grid__footer">
      <div><strong>Total Allocated:</strong> {{ totalAllocated }}</div>
      <div
        v-if="Number(allocationDifference) < 0 && newTrip"
        class="allocation-difference"
      >
        Allocation is over by {{ Math.abs(Number(allocationDifference)) }}
      </div>
      <div
        v-else-if="Number(allocationDifference) > 0 && newTrip"
        class="allocation-difference"
      >
        Allocation is under by {{ allocationDifference }}
      </div>
      <div>
        <strong>Total Expenses:</strong> {{ tripLogStore.totalExpenses }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import { useTripLogStore } from "@/stores/useTripLogStore";
import { Grid } from "@progress/kendo-vue-grid";
import { computed, ref, watchEffect } from "vue";
import { NumericTextBox } from "@progress/kendo-vue-inputs";
import GridController from "@/shared/services/GridController";
import type { TripVisitDisplay } from "@/Domain/TripVisitDisplay";

const tripLogStore = useTripLogStore();
const gridRef = ref<InstanceType<typeof Grid> | null>(null);

defineProps<{
  loading: boolean;
  newTrip: boolean;
}>();

const columns = ref([
  {
    field: "VisitId",
    title: "Visit ID",
    minWidth: 70,
  },
  {
    field: "VisitType",
    title: "Type",
    minWidth: 70,
  },
  {
    field: "VisitDate",
    title: "Visit Date",
    type: "date",
    format: "{0:MM/dd/yyyy}",
    minWidth: 100,
  },
  {
    field: "Plant",
    title: "Plant",
    minWidth: 100,
  },
  {
    title: "Time Getting To Plant (HH:MM)",
    cell: "TimeGettingToPlantCell",
    minWidth: 100,
  },
  {
    title: "Time In Plant (HH:MM)",
    cell: "TimeInPlantCell",
    minWidth: 100,
  },
  {
    title: "Time Getting Home (HH:MM)",
    cell: "TimeGettingHomeCell",
    minWidth: 100,
  },
  {
    title: "Allocation of Total Expenses",
    cell: "AllocationOfTotalExpensesCell",
    minWidth: 100,
  },
  {
    title: "Comment",
    cell: "CommentCell",
    minWidth: 100,
  },
] as GridColumnPropsExtended[]);

const controller = new GridController<TripVisitDisplay>(
  "TripLogVisitsGrid",
  columns.value
);
controller.initOptions();

const totalAllocated = computed(() => {
  return (
    tripLogStore.tripVisits?.reduce((acc, visit) => {
      return acc + (visit.AllocationOfTotalExpenses || 0);
    }, 0) ?? 0
  ).toFixed(2);
});

const allocationDifference = computed(() => {
  return (
    Number(tripLogStore.totalExpenses) - Number(totalAllocated.value)
  ).toFixed(2);
});

watchEffect(() => {
  if (gridRef.value && !controller.gridRef.value) {
    controller.setGridRef(gridRef);
  }
});
</script>

<style lang="scss">
.trip-log-visits-grid {
  .k-input-suffix {
    font-weight: bold;
    font-size: 12px;
    margin-right: 4px;
  }

  &__input {
    input {
      padding: 4px !important;
    }
  }
}
</style>

<style scoped lang="scss">
.trip-log-visits-grid {
  &__grid {
    min-height: 200px;
  }
  &__cell {
    padding: 0;
  }

  &__input-wrapper {
    display: flex;
    align-items: center;
    flex-direction: column;
  }

  &__input {
    border: 1px solid #ccc;
    border-radius: 4px;
  }

  &__allocation-input {
    padding: 4px;
    border: 1px solid #ccc;
    border-radius: 4px;
    max-width: 100%;
  }

  .allocation-difference {
    color: red;
    font-weight: bold;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
  }

  &__comment {
    resize: vertical;
  }
}
</style>
